import { useCallback, useMemo, useRef, type RefObject } from "react";
import type { TFunction } from "i18next";
import type { Bounds, NowPlaying, TimelineWindow, Waveform } from "../../api/types";
import { usePlayer } from "../../state/player";
import type { Scene } from "../draw";
import {
  MAX_MS_PER_PX,
  MIN_MS_PER_PX,
  type View,
  clampToBounds,
  clampZoom,
  withinArchive,
} from "../geometry";
import type { SnapKind } from "../snap";

/** An edge being dragged, from the grab until the pointer comes up. */
export interface Draft {
  id: number;
  edge: "start" | "end";
  startMs: number;
  endMs: number;
  /** The stored span, kept so the drag can be undone and so a no-op drag saves nothing. */
  originalStartMs: number;
  originalEndMs: number;
  snap: SnapKind | null;
}

/**
 * The mutable substrate the canvas, the gestures and the landings all share.
 *
 * The view lives in a ref, not in state: panning updates it on every pointer move and a
 * React render per frame would make dragging a seven-day window stutter. Only the
 * quantised fetch window is mirrored into state, and that changes rarely. Everything the
 * animation loop reads is here for the same reason -- it runs outside React and must not
 * re-subscribe to get a current value.
 */
export interface TimelineRefs {
  container: RefObject<HTMLDivElement | null>;
  canvas: RefObject<HTMLCanvasElement | null>;
  view: RefObject<View>;
  scene: RefObject<Scene | null>;
  data: RefObject<TimelineWindow | null>;
  dirty: RefObject<boolean>;
  hover: RefObject<number | null>;
  draft: RefObject<Draft | null>;
  /**
   * Monotonic id for the most recent landing, click or scrub, so a superseded one drops its
   * result: both wait on a lookup, and the newest gesture is the one the player must obey.
   *
   * Bumped by a landing starting and by a pointer going down, because the newest gesture is
   * the newest claim whether or not it has produced a landing yet. Without the second, a
   * lookup still in flight when the next press comes down lands in the middle of it.
   */
  landing: RefObject<number>;
  /**
   * Where a pan is aiming, while one is in progress; null the rest of the time.
   *
   * The centre bar is the playhead and the gesture drags the content beneath it -- that is the
   * conceit the whole timeline is built on, and during a drag it is the only reading that makes
   * sense: the store still holds the instant the audio is at, which is precisely the instant
   * the viewer is dragging away from. Drawing that one would slide the bar off the centre for
   * the length of the gesture and snap it back on release.
   *
   * Held here rather than written to the store because the audio goes on reporting its own
   * position throughout, four times a second, and would overwrite anything the pan put there
   * the moment the pointer stopped moving. The landing is what makes the aim true; until it
   * resolves the bar keeps drawing it, so there is no frame between release and the new
   * position where the bar points back at the old one.
   */
  aim: RefObject<number | null>;
  /**
   * Whether a pan is in progress, which is the one gesture that moves the bar continuously.
   *
   * The selection is derived from where the bar is drawn (see the render loop), and during a
   * drag the bar sweeps every block between where the gesture started and where it is now. A
   * border stepping through each of them at sixty frames a second is not a selection anybody
   * made -- it is a flicker -- and the store write behind it wakes every subscriber for a value
   * that is superseded on the next move. The bar keeps drawing the aim throughout; only the
   * derivation is held back, and released on the frame after the pointer comes up, where the
   * aim has settled on what the gesture actually landed on.
   */
  panning: RefObject<boolean>;
  waveform: RefObject<Waveform | null>;
  nowPlaying: RefObject<NowPlaying | null>;
  bounds: RefObject<Bounds | null>;
  liveDelay: RefObject<number>;
  snap: RefObject<{ icy: number[]; scheduled: number[] }>;
}

/**
 * One ref holding the boxes, rather than sixteen refs: a ref is only a mutable box, and one
 * object of them keeps a stable identity that the extracted hooks can list as a dependency
 * without tearing their effects down on every render.
 */
export function useTimelineRefs(): TimelineRefs {
  const bag = useRef<TimelineRefs | null>(null);
  bag.current ??= {
    container: { current: null },
    canvas: { current: null },
    view: { current: { centerMs: Date.now(), msPerPx: 4_000, width: 0 } },
    scene: { current: null },
    data: { current: null },
    dirty: { current: true },
    hover: { current: null },
    draft: { current: null },
    landing: { current: 0 },
    aim: { current: null },
    panning: { current: false },
    waveform: { current: null },
    nowPlaying: { current: null },
    bounds: { current: null },
    liveDelay: { current: 0 },
    snap: { current: { icy: [], scheduled: [] } },
  };
  return bag.current;
}

/** The four ends of the world, asked in the four ways the timeline needs them asked. */
export interface TimelineWalls {
  /**
   * The hold-back the *view* is walled at: how far behind the clock this deployment's live
   * stream sits, while the live stream is what is playing, and nothing at all otherwise.
   *
   * One number for the pan stop, the click test, the follow target and the drawn band, so they
   * cannot disagree about where the end of the world is -- and it exists only when something is
   * drawing it. A wall nobody can see is a pan that stops short of a timeline plainly carrying
   * on, so off live the view reaches the now marker; a gesture that arrives there is reaching
   * for the live edge and is taken live, and the band appears and says why it goes no further.
   *
   * `liveLagMs` rather than the published figure, because the published one leaves a strip
   * between the centre bar and the band: audio the archive serves for a few seconds and then
   * runs out of, which is not somewhere the playhead can come to rest. Remembered rather than
   * recomputed, so the wall does not shift under a listener who pauses.
   */
  wallDelayMs: () => number;
  /**
   * Every view change goes through this. The live edge is a wall, not a suggestion, and until
   * now only dragging respected it -- wheeling and the zoom buttons could leave the view sitting
   * in the future.
   *
   * The far wall is the first segment on record, not the first byte still in the buffer. The
   * timeline is a history of what was broadcast and most of it is older than the rolling
   * buffer, so stopping the pan where the audio stops would put years of shows behind a wall
   * -- and pinning the centre there is what yanked the view back to the buffer start every
   * time an older track was clicked.
   */
  clampView: (next: View) => View;
  /**
   * Whether an instant is inside audio a bare click could open a playlist over.
   *
   * A narrower interval than `clampView` walls, and deliberately so: panning is about what is
   * on record, this is about what can be played from nothing but a timestamp. Only the buffer
   * answers that -- an exported show older than the buffer plays from its own file, but that
   * needs the block, and there is no window playlist to cut over bare canvas there.
   *
   * The archive's own published hold-back, always, and never the view's: the recorder is still
   * appending to the newest hour file and a window cut over bytes it has not written comes back
   * 404. The view may reach the clock; a playlist may not. `landOn` takes the difference live,
   * which is the only thing out there anyway.
   */
  inArchive: (ms: number) => boolean;
  /**
   * Whether an instant is anywhere on record, and so somewhere the playhead may be parked.
   *
   * The interval `clampView` walls, asked about an arbitrary x rather than the centre --
   * exactly the relationship `inArchive` has with a bare window playlist. The two are
   * different questions and were being answered by one predicate: "can a playlist be cut
   * over nothing but this timestamp" is narrower than "is this a moment the station has a
   * record of", and gating the landing on the narrow one is what stopped a pan from putting
   * the playhead on an archived show. The bar is the centre of the view by construction, so
   * refusing to move it does not leave it somewhere else honestly -- it leaves it lying.
   */
  onRecord: (ms: number) => boolean;
}

export function useTimelineWalls(r: TimelineRefs): TimelineWalls {
  const wallDelayMs = useCallback((): number => {
    const player = usePlayer.getState();
    return player.source?.kind === "live" ? Math.max(r.liveDelay.current, player.liveLagMs) : 0;
  }, [r]);
  const clampView = useCallback(
    (next: View): View => {
      const data = r.data.current;
      const nowMs = Date.now();
      const archiveStart = r.bounds.current?.archive_start;
      return clampToBounds(
        next,
        archiveStart ? Date.parse(archiveStart) : null,
        data?.latest ? Date.parse(data.latest) : null,
        nowMs,
        wallDelayMs(),
      );
    },
    [r, wallDelayMs],
  );
  const inArchive = useCallback(
    (ms: number): boolean => {
      const data = r.data.current;
      const nowMs = Date.now();
      const bufferStart = r.bounds.current?.buffer_start;
      return withinArchive(
        ms,
        bufferStart ? Date.parse(bufferStart) : null,
        data?.latest ? Date.parse(data.latest) : null,
        nowMs,
        r.liveDelay.current,
      );
    },
    [r],
  );
  const onRecord = useCallback(
    (ms: number): boolean => {
      const data = r.data.current;
      const nowMs = Date.now();
      const archiveStart = r.bounds.current?.archive_start;
      return withinArchive(
        ms,
        archiveStart ? Date.parse(archiveStart) : null,
        data?.latest ? Date.parse(data.latest) : null,
        nowMs,
        wallDelayMs(),
      );
    },
    [r, wallDelayMs],
  );
  return useMemo(
    () => ({ wallDelayMs, clampView, inArchive, onRecord }),
    [wallDelayMs, clampView, inArchive, onRecord],
  );
}

/**
 * What every layer outside the component reads. One object, memoised once, so an effect that
 * depends on it is not torn down and rebuilt on a render that changed nothing it uses.
 */
export interface TimelineCtx {
  refs: TimelineRefs;
  walls: TimelineWalls;
  latestMs: () => number | null;
  syncWindow: () => void;
  editable: boolean;
  t: TFunction;
}

/**
 * Every write to the substrate.
 *
 * Plain functions rather than assignments at the call site, and that is not a style choice:
 * React's compiler freezes whatever a hook is handed, so a hook may not assign through the
 * bundle it was given. The module that owns the shape does the writing and the hooks ask for
 * it, which is also the only place the "mark the canvas stale" half is guaranteed not to be
 * forgotten.
 */
export function markDirty(r: TimelineRefs): void {
  r.dirty.current = true;
}

export function setView(r: TimelineRefs, next: View): void {
  r.view.current = next;
  markDirty(r);
}

export function setViewWidth(r: TimelineRefs, width: number): void {
  setView(r, { ...r.view.current, width });
}

export function setWaveform(r: TimelineRefs, wave: Waveform | null): void {
  r.waveform.current = wave;
  markDirty(r);
}

export function setNowPlaying(r: TimelineRefs, playing: NowPlaying | null): void {
  r.nowPlaying.current = playing;
  markDirty(r);
}

export function setBounds(r: TimelineRefs, bounds: Bounds | null): void {
  r.bounds.current = bounds;
  markDirty(r);
}

export function setLiveDelay(r: TimelineRefs, ms: number): void {
  r.liveDelay.current = ms;
  markDirty(r);
}

export function setSnapTargets(r: TimelineRefs, icy: number[], scheduled: number[]): void {
  r.snap.current = { icy, scheduled };
}

/** The previous window stays on screen until the next one arrives rather than blanking. */
export function setWindowData(r: TimelineRefs, data: TimelineWindow | undefined): void {
  if (!data) return;
  r.data.current = data;
  markDirty(r);
}

/** Set the zoom from a span the toolbar names, keeping the centre where it is. */
export function applySpan(
  r: TimelineRefs,
  walls: TimelineWalls,
  syncWindow: () => void,
  spanMs: number,
): void {
  const width = r.view.current.width || 1;
  setView(r, walls.clampView({ ...r.view.current, msPerPx: clampZoom(spanMs / width) }));
  syncWindow();
}

/** Zoom by a factor about the centre, which is where the playhead is. */
export function applyZoom(
  r: TimelineRefs,
  walls: TimelineWalls,
  syncWindow: () => void,
  factor: number,
): void {
  const { msPerPx } = r.view.current;
  setView(
    r,
    walls.clampView({
      ...r.view.current,
      msPerPx: clampZoom(Math.min(MAX_MS_PER_PX, Math.max(MIN_MS_PER_PX, msPerPx * factor))),
    }),
  );
  syncWindow();
}
